import { z } from 'zod';

const uuid = z.string().uuid();

/**
 * Who an announcement is for (scoping 3.9): the whole school, a sede, a grade, a group or one
 * student's family.
 */
export const announcementAudienceSchema = z.enum(['all', 'sede', 'grade', 'group', 'family']);

/**
 * Body of `POST /announcements`: the message, its audience, and whether to publish now, at a
 * time, or keep it as a draft.
 */
export const createAnnouncementInputSchema = z
  .object({
    title: z.string().trim().min(1).max(160),
    body: z.string().trim().min(1).max(20_000),
    audience: announcementAudienceSchema.default('all'),
    targetId: uuid.optional(),
    publish: z.enum(['now', 'schedule', 'draft']).default('draft'),
    scheduledAt: z.string().datetime().optional(),
  })
  .refine(
    (input) => input.audience === 'all' || input.targetId !== undefined,
    'targetId is required for this audience',
  )
  .refine(
    (input) => input.publish !== 'schedule' || input.scheduledAt !== undefined,
    'scheduledAt is required to schedule',
  );

/**
 * Body of `PATCH /announcements/{id}`: title and body always; audience and schedule while not
 * published.
 */
export const updateAnnouncementInputSchema = z
  .object({
    title: z.string().trim().min(1).max(160),
    body: z.string().trim().min(1).max(20_000),
    audience: announcementAudienceSchema,
    targetId: uuid.nullable(),
    scheduledAt: z.string().datetime().nullable(),
  })
  .partial();

/**
 * Body of `POST /announcements/{id}/attachments`: an object already uploaded through the
 * presigned URL (purpose `attachment`).
 */
export const registerAttachmentInputSchema = z.object({
  key: z.string().min(1).max(500),
  filename: z.string().trim().min(1).max(200),
  contentType: z
    .string()
    .trim()
    .regex(/^[\w.+-]+\/[\w.+-]+$/, 'contentType must be a media type'),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(50 * 1024 * 1024),
});

/**
 * Query of `GET /announcements` (management list).
 */
export const announcementsQuerySchema = z.object({
  status: z.enum(['draft', 'scheduled', 'published', 'archived']).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});

/**
 * An audience kind.
 */
export type AnnouncementAudience = z.infer<typeof announcementAudienceSchema>;
/**
 * Body of `POST /announcements`.
 */
export type CreateAnnouncementInput = z.input<typeof createAnnouncementInputSchema>;
/**
 * Body of `PATCH /announcements/{id}`.
 */
export type UpdateAnnouncementInput = z.input<typeof updateAnnouncementInputSchema>;
/**
 * Body of `POST /announcements/{id}/attachments`.
 */
export type RegisterAttachmentInput = z.input<typeof registerAttachmentInputSchema>;
/**
 * Query of `GET /announcements`.
 */
export type AnnouncementsQuery = z.infer<typeof announcementsQuerySchema>;

/**
 * An attachment as the API lists it (the download link is a separate call).
 */
export type AttachmentView = {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
};

/**
 * An announcement as the school manages it.
 */
export type AnnouncementView = {
  id: string;
  title: string;
  body: string;
  audience: AnnouncementAudience;
  targetId: string | null;
  targetName: string | null;
  status: 'draft' | 'scheduled' | 'published' | 'archived';
  scheduledAt: string | null;
  publishedAt: string | null;
  authorName: string | null;
  attachments: AttachmentView[];
  recipients: { total: number; read: number; emailed: number };
  createdAt: string;
  updatedAt: string;
};

/**
 * One recipient with the receipts of every channel.
 */
export type RecipientView = {
  userId: string;
  name: string;
  email: string | null;
  studentName: string | null;
  readAt: string | null;
  emailStatus: string | null;
  emailSentAt: string | null;
};

/**
 * An announcement as a recipient sees it, with their own read receipt.
 */
export type FeedItem = {
  id: string;
  title: string;
  body: string;
  audience: AnnouncementAudience;
  targetName: string | null;
  publishedAt: string;
  authorName: string | null;
  attachments: AttachmentView[];
  readAt: string | null;
};
